import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent, TokenSource } from 'livekit-client';
import { AppConfig } from '@/app-config';
import { toastAlert } from '@/components/livekit/alert-toast';
import { getVisitorInfo } from '@/lib/visitor-tracking';

export function useEmbedRoom(appConfig: AppConfig) {
  const aborted = useRef(false);
  const [room, setRoom] = useState(() => new Room());
  const [isSessionActive, setIsSessionActive] = useState(false);
  const connectPromise = useRef<Promise<void> | null>(null);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function onDisconnected() {
      setIsSessionActive(false);
    }

    function onMediaDevicesError(error: Error) {
      toastAlert({
        title: 'Encountered an error with your media devices',
        description: `${error.name}: ${error.message}`,
      });
    }

    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
    };
  }, [room]);

  useEffect(() => {
    return () => {
      aborted.current = true;
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
      room.disconnect();
    };
  }, [room]);

  const tokenSource = useMemo(
    () =>
      TokenSource.custom(async () => {
        const url = new URL(
          process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details',
          window.location.origin
        );

        try {
          const res = await fetch(url.toString(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Sandbox-Id': appConfig.sandboxId ?? '',
            },
            body: JSON.stringify({
              room_config: appConfig.agentName
                ? {
                  agents: [{ agent_name: appConfig.agentName }],
                }
                : undefined,
                workflow_type: 'web',
                visitor_id: getVisitorInfo().visitor_id,
                timezone: getVisitorInfo().timezone,
                language: getVisitorInfo().language,
            }),
          });
          return await res.json();
        } catch (error) {
          console.error('Error fetching connection details:', error);
          throw new Error('Error fetching connection details!');
        }
      }),
    [appConfig]
  );

  const connectRoom = useCallback(async () => {
    if (room.state !== 'disconnected' || connectPromise.current) {
      return connectPromise.current ?? Promise.resolve();
    }

    const { isPreConnectBufferEnabled } = appConfig;

    connectPromise.current = tokenSource
      .fetch({ agentName: appConfig.agentName })
      .then((connectionDetails) =>
        room.connect(connectionDetails.serverUrl, connectionDetails.participantToken)
      )
      .then(() =>
        room.localParticipant.setMicrophoneEnabled(false, undefined, {
          preConnectBuffer: isPreConnectBufferEnabled,
        })
      )
      .then(() => {})
      .catch((error) => {
        if (aborted.current) {
          return;
        }

        toastAlert({
          title: 'There was an error connecting to the agent',
          description: `${error.name}: ${error.message}`,
        });
      })
      .finally(() => {
        connectPromise.current = null;
      });

    return connectPromise.current;
  }, [room, appConfig, tokenSource]);

  useEffect(() => {
    // void connectRoom();
  }, [connectRoom]);

  // Store latest endSession in ref to avoid stale closure
  const endSessionRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // const endSession = useCallback(async () => {
  //   setIsSessionActive(false);
  //   if (sessionTimeoutRef.current) {
  //     clearTimeout(sessionTimeoutRef.current);
  //     sessionTimeoutRef.current = null;
  //   }
    
  //   try {
  //     await room.disconnect();
  //   } catch (error) {
  //     console.warn('Error during disconnect:', error);
  //   }
    
  //   // Create new Room instance to allow reconnection
  //   setRoom(new Room());
  // }, [room]);

  const endSession = useCallback(async () => {
    setIsSessionActive(false);
    
    // Clear timeout
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    try {
      if (room.state === 'connected') {
        try {
          await room.localParticipant.publishData(
            new TextEncoder().encode('end_conversation'),
            { reliable: true }
          );
          // Brief delay to ensure message is sent
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          console.warn('Failed to send end signal:', err);
        }
      }
      // Disable microphone and disconnect
      await Promise.allSettled([
        room.localParticipant.setMicrophoneEnabled(false),
        room.disconnect()
      ]);
      
    } catch (error) {
      console.warn('Error during disconnect:', error);
    } finally {
      // Always create new Room instance to allow reconnection
      setRoom(new Room());
    }
  }, [room]);

  // Keep ref updated with latest endSession
  useEffect(() => {
    endSessionRef.current = endSession;
  }, [endSession]);

  const startSession = useCallback(async () => {
    setIsSessionActive(true);
    const { isPreConnectBufferEnabled } = appConfig;

    // Clear any existing timeout
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }

    // Use ref to always get latest endSession
    sessionTimeoutRef.current = setTimeout(() => {
      console.log('Session timeout reached (4 minutes), ending session automatically');
      endSessionRef.current?.();
    }, 4 * 60 * 1000);

    return connectRoom()
      .then(() => {
          console.log('Room connected, keeping microphone muted...');
          return room.localParticipant.setMicrophoneEnabled(false, undefined, {
            preConnectBuffer: isPreConnectBufferEnabled,
          });
     
      })
      .catch((error) => {
        if (aborted.current) {
          return;
        }

        toastAlert({
          title: 'There was an error enabling your microphone',
          description: `${error.name}: ${error.message}`,
        });
      });
  }, [appConfig, connectRoom, room]);

  return { room, isSessionActive, startSession, endSession };
}